import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatDateTime, formatStatus, getStatusColor } from '@/lib/utils';
import { Plus, FileText, Check, Send, ChevronRight, ChevronDown, Trash2, Clock, Download, Search, UserPlus, Edit2, Copy } from 'lucide-react';
import { Pagination } from '@/components/Pagination';
import { DWRApprovalInbox } from '@/components/DWRApprovalInbox';

const DWR_STATUSES = ['DRAFT', 'SUBMITTED', 'APPROVED'];
const PAGE_SIZE = 50;

export default function DWRPage() {
  const { user } = useAuth();
  const [dwrs, setDwrs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taskItems, setTaskItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editDwr, setEditDwr] = useState(null);
  const [selectedDwr, setSelectedDwr] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchDwrs = (p = page) => {
    const params = new URLSearchParams();
    if (filterProject) params.set('project_id', filterProject);
    if (filterStatus) params.set('status', filterStatus);
    params.set('skip', ((p - 1) * PAGE_SIZE).toString());
    params.set('limit', PAGE_SIZE.toString());
    api.get(`/dwr?${params}`).then(r => { setDwrs(r.data.dwrs || []); setTotalCount(r.data.total_count || 0); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchDwrs(page); }, [filterProject, filterStatus, page]);
  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects || []));
    api.get('/dwr/task-items').then(r => setTaskItems(r.data.task_items || {}));
  }, []);

  const canCreate = ['DIRECTOR', 'SUPERVISOR'].includes(user?.role);

  const viewDwr = async (dwrId) => {
    try {
      const res = await api.get(`/dwr/${dwrId}`);
      setSelectedDwr(res.data.dwr);
    } catch (err) { alert('Failed to load DWR'); }
  };

  const approveDwr = async (dwrId) => {
    if (!window.confirm('Approve this DWR?')) return;
    try {
      await api.post(`/dwr/${dwrId}/approve`);
      fetchDwrs();
      if (selectedDwr?.id === dwrId) viewDwr(dwrId);
    } catch (err) { alert(err.response?.data?.detail || 'Approval failed'); }
  };

  const submitDwr = async (dwrId) => {
    if (!window.confirm('Submit this DWR? Attendance records will be auto-generated.')) return;
    try {
      await api.post(`/dwr/${dwrId}/submit`);
      fetchDwrs();
      if (selectedDwr?.id === dwrId) viewDwr(dwrId);
    } catch (err) { alert(err.response?.data?.detail || 'Submission failed'); }
  };

  const deleteDwr = async (dwrId) => {
    if (!window.confirm('Delete this DWR? This cannot be undone.')) return;
    try {
      await api.delete(`/dwr/${dwrId}`);
      if (selectedDwr?.id === dwrId) setSelectedDwr(null);
      fetchDwrs();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const canManageDraft = (d) => d?.status === 'DRAFT' && (d.supervisor_id === user?.id || user?.role === 'DIRECTOR');

  const openEditDwr = async (dwrId) => {
    try {
      const res = await api.get(`/dwr/${dwrId}`);
      setEditDwr(res.data.dwr);
    } catch (err) { alert('Failed to load DWR for editing'); }
  };

  // DWR-03: PDF download
  const downloadPdf = async (dwrId) => {
    try {
      const res = await api.get(`/dwr/${dwrId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DWR_${dwrId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('PDF download failed'); }
  };

  return (
    <div data-testid="dwr-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Daily Work Reports</h1>
          <p className="text-sm text-slate-500" data-testid="dwr-total-count">{totalCount} reports</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="create-dwr-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> New DWR
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="dwr-filter-project"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="dwr-filter-status"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Statuses</option>
          {DWR_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
        </select>
      </div>

      {user?.role === 'DIRECTOR' && <DWRApprovalInbox onChanged={() => { fetchDwrs(); if (selectedDwr) viewDwr(selectedDwr.id); }} onView={viewDwr} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm md:min-w-[760px]">
                <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left py-3 px-2 md:px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                  <th className="text-left py-3 px-2 md:px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</th>
                  <th className="hidden md:table-cell text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Supervisor</th>
                  <th className="hidden md:table-cell text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Workers</th>
                  <th className="text-left py-3 px-2 md:px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                  <th className="text-left py-3 px-1 md:px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                </tr></thead>
                <tbody>
                  {dwrs.map(d => (
                    <tr key={d.id} className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${selectedDwr?.id === d.id ? 'bg-blue-50' : ''}`}
                      onClick={() => viewDwr(d.id)} data-testid={`dwr-row-${d.id}`}>
                      <td className="py-2.5 px-2 md:px-4 text-slate-900 font-medium whitespace-nowrap text-xs md:text-sm">{formatDate(d.date)}</td>
                      <td className="py-2.5 px-2 md:px-4 text-xs text-[#1B5486] font-medium">{d.project_code || '-'}</td>
                      <td className="hidden md:table-cell py-2.5 px-4 text-slate-500">{d.supervisor_name || '-'}</td>
                      <td className="hidden md:table-cell py-2.5 px-4 text-center">{d.employee_count || 0}</td>
                      <td className="py-2.5 px-2 md:px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(d.status)}`}>{formatStatus(d.status)}</span></td>
                      <td className="py-2.5 px-1 md:px-2">
                        {canManageDraft(d) && (
                          <div className="flex gap-1 sm:gap-1.5 whitespace-nowrap">
                            <button onClick={(e) => { e.stopPropagation(); openEditDwr(d.id); }} className="flex items-center gap-1 p-1 sm:px-2 sm:py-1 rounded border border-[#1B5486]/30 text-[#1B5486] text-xs hover:bg-[#1B5486]/5" title="Edit" data-testid={`edit-dwr-${d.id}`}><Edit2 size={12} /><span className="hidden sm:inline">Edit</span></button>
                            <button onClick={(e) => { e.stopPropagation(); deleteDwr(d.id); }} className="flex items-center gap-1 p-1 sm:px-2 sm:py-1 rounded border border-red-200 text-red-500 text-xs hover:bg-red-50" title="Delete" data-testid={`delete-dwr-${d.id}`}><Trash2 size={12} /><span className="hidden sm:inline">Delete</span></button>
                            <button onClick={(e) => { e.stopPropagation(); submitDwr(d.id); }} className="p-1 text-blue-600 hover:text-blue-800" title="Submit" data-testid={`submit-dwr-${d.id}`}><Send size={14} /></button>
                          </div>
                        )}
                        {d.status === 'SUBMITTED' && user?.role === 'DIRECTOR' && (
                          <button onClick={(e) => { e.stopPropagation(); approveDwr(d.id); }} className="flex items-center gap-1 px-2 py-1 rounded border border-emerald-300 text-emerald-700 text-xs hover:bg-emerald-50" title="Approve" data-testid={`approve-dwr-${d.id}`}><Check size={12} /><span className="hidden sm:inline">Approve</span></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {dwrs.length === 0 && <div className="text-center py-12 text-slate-400">No DWRs found</div>}
              <Pagination currentPage={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </div>
          )}
        </div>

        <div>
          {selectedDwr ? (
            <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4" data-testid="dwr-detail-panel">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">DWR Detail</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(selectedDwr.status)}`}>{formatStatus(selectedDwr.status)}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div><span className="text-slate-500">Date:</span> <span className="font-medium">{formatDate(selectedDwr.date)}</span></div>
                <div><span className="text-slate-500">Project:</span> <span className="font-medium">{selectedDwr.project?.project_code} - {selectedDwr.project?.name}</span></div>
                {selectedDwr.client_name && <div><span className="text-slate-500">Client:</span> <span>{selectedDwr.client_name}</span></div>}
                {selectedDwr.notes && <div><span className="text-slate-500">Notes:</span> <span>{selectedDwr.notes}</span></div>}
              </div>

              {selectedDwr.employee_entries?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Employee Entries</h4>
                  <div className="space-y-2">
                    {selectedDwr.employee_entries.map(e => (
                      <div key={e.id} className="p-2 bg-slate-50 rounded text-xs">
                        <div className="font-medium text-slate-900">{e.employee_name}</div>
                        <div className="text-slate-500">{e.task_assigned} | {e.in_time} - {e.out_time} ({e.hours_worked}h)</div>
                        {e.completion_remark && <div className="text-slate-500 mt-1">{e.completion_remark}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedDwr.work_summary?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Work Summary</h4>
                  <div className="space-y-1">
                    {selectedDwr.work_summary.map(w => (
                      <div key={w.id} className="flex justify-between text-xs p-2 bg-slate-50 rounded">
                        <span>{w.task_name} - {w.item}</span>
                        <span className="font-medium">{w.quantity_done} {w.uom}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {/* DWR-03: Download PDF button */}
                <button onClick={() => downloadPdf(selectedDwr.id)} data-testid="download-dwr-pdf-btn"
                  className="flex-1 border border-[#1B5486] text-[#1B5486] py-2 rounded-md text-xs font-medium hover:bg-[#1B5486]/5 flex items-center justify-center gap-1">
                  <Download size={12} /> Download PDF
                </button>
                {canManageDraft(selectedDwr) && (
                  <>
                    <button onClick={() => openEditDwr(selectedDwr.id)} className="flex-1 border border-[#1B5486] text-[#1B5486] py-2 rounded-md text-xs font-medium hover:bg-[#1B5486]/5 flex items-center justify-center gap-1">
                      <Edit2 size={12} /> Edit
                    </button>
                    <button onClick={() => deleteDwr(selectedDwr.id)} className="flex-1 border border-red-300 text-red-500 py-2 rounded-md text-xs font-medium hover:bg-red-50 flex items-center justify-center gap-1">
                      <Trash2 size={12} /> Delete
                    </button>
                    <button onClick={() => submitDwr(selectedDwr.id)} className="flex-1 bg-blue-600 text-white py-2 rounded-md text-xs font-medium hover:bg-blue-700 flex items-center justify-center gap-1">
                      <Send size={12} /> Submit
                    </button>
                  </>
                )}
                {selectedDwr.status === 'SUBMITTED' && user?.role === 'DIRECTOR' && (
                  <button onClick={() => approveDwr(selectedDwr.id)} className="flex-1 bg-green-600 text-white py-2 rounded-md text-xs font-medium hover:bg-green-700 flex items-center justify-center gap-1">
                    <Check size={12} /> Approve
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-sm text-slate-400">
              Select a DWR to view details
            </div>
          )}
        </div>
      </div>

      {(showCreate || editDwr) && <CreateDWRModal projects={projects} taskItems={taskItems} editData={editDwr} onClose={() => { setShowCreate(false); setEditDwr(null); }} onCreated={() => { setShowCreate(false); setEditDwr(null); fetchDwrs(); if (selectedDwr) viewDwr(selectedDwr.id); }} />}
    </div>
  );
}

// DWR-01: Searchable Employee Dropdown with 'Other' option
function EmployeeSearchDropdown({ value, employeeId, employees, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isOther, setIsOther] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Initialize display value
  useEffect(() => {
    if (employeeId === '__OTHER__') {
      setIsOther(true);
    }
  }, [employeeId]);

  const filtered = employees.filter(em =>
    em.name.toLowerCase().includes(search.toLowerCase()) ||
    em.employee_code.toLowerCase().includes(search.toLowerCase())
  );

  const selectEmployee = (emp) => {
    setIsOther(false);
    setOpen(false);
    setSearch('');
    onChange(emp.id, emp.name);
  };

  const selectOther = () => {
    setIsOther(true);
    setOpen(false);
    setSearch('');
    onChange('__OTHER__', '');
  };

  if (isOther) {
    return (
      <div className="flex gap-1 items-center" data-testid="emp-other-input">
        <input placeholder="Name - Role" value={value} onChange={e => onChange('', e.target.value)}
          className="input-field text-xs flex-1" data-testid="emp-other-name" />
        <button type="button" onClick={() => { setIsOther(false); onChange('', ''); }}
          className="text-[10px] text-slate-400 hover:text-red-500 shrink-0 px-1" title="Back to dropdown">x</button>
      </div>
    );
  }

  const displayName = employeeId ? employees.find(e => e.id === employeeId)?.name || value : '';

  return (
    <div ref={ref} className="relative" data-testid="emp-search-dropdown">
      <div className="input-field text-xs cursor-pointer flex items-center gap-1 min-h-[32px]"
        onClick={() => setOpen(!open)}>
        <span className={`flex-1 truncate ${displayName ? 'text-slate-900' : 'text-slate-400'}`}>
          {displayName || 'Select Employee'}
        </span>
        <ChevronDown size={12} className="text-slate-400 shrink-0" />
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-hidden" data-testid="emp-dropdown-list">
          <div className="p-1.5 border-b border-slate-100">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search employees..." className="w-full pl-6 pr-2 py-1.5 text-xs border border-slate-200 rounded outline-none"
                data-testid="emp-search-input" />
            </div>
          </div>
          <div className="overflow-y-auto max-h-40">
            {filtered.map(em => (
              <div key={em.id} onClick={() => selectEmployee(em)}
                className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-blue-50 ${employeeId === em.id ? 'bg-blue-50 text-[#1B5486] font-semibold' : 'text-slate-700'}`}
                data-testid={`emp-option-${em.id}`}>
                {em.name} <span className="text-slate-400">({em.employee_code})</span>
              </div>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No employees found</div>}
          </div>
          <div className="border-t border-slate-100">
            <div onClick={selectOther}
              className="px-3 py-2 text-xs cursor-pointer hover:bg-amber-50 text-amber-700 flex items-center gap-1 font-medium"
              data-testid="emp-option-other">
              <UserPlus size={12} /> Other / Unregistered
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateDWRModal({ projects, taskItems, editData, onClose, onCreated }) {
  const isEdit = !!editData;
  const [form, setForm] = useState(
    isEdit
      ? { project_id: editData.project_id || '', date: editData.date || '', notes: editData.notes || '' }
      : { project_id: '', date: new Date().toLocaleDateString('en-CA'), notes: '' }
  );
  const [entries, setEntries] = useState(
    isEdit && editData.employee_entries?.length
      ? editData.employee_entries.map(e => ({ employee_id: e.employee_id || '', employee_name: e.employee_name || '', task_assigned: e.task_assigned || '', in_time: e.in_time || '09:00', out_time: e.out_time || '17:00', completion_remark: e.completion_remark || '' }))
      : [{ employee_id: '', employee_name: '', task_assigned: '', in_time: '09:00', out_time: '17:00', completion_remark: '' }]
  );
  const [summaries, setSummaries] = useState(
    isEdit && editData.work_summary?.length
      ? editData.work_summary.map(s => ({ task_name: s.task_name || '', item: s.item || '', quantity_done: s.quantity_done || '', uom: s.uom || 'Nos' }))
      : [{ task_name: '', item: '', quantity_done: '', uom: 'Nos' }]
  );
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copiedFrom, setCopiedFrom] = useState('');

  useEffect(() => { api.get('/employees').then(r => setEmployees(r.data.employees || [])).catch(() => {}); }, []);

  const taskNames = Object.keys(taskItems);

  const copyPrevious = async () => {
    try {
      const params = new URLSearchParams({ before: form.date });
      if (form.project_id) params.set('project_id', form.project_id);
      const { data } = await api.get(`/dwr/last-mine?${params}`);
      const prev = data.dwr;
      setForm(f => ({ ...f, project_id: prev.project_id || f.project_id }));
      if (prev.employee_entries?.length) {
        setEntries(prev.employee_entries.map(e => ({ employee_id: e.employee_id || (e.employee_name ? '__OTHER__' : ''), employee_name: e.employee_name || '', task_assigned: e.task_assigned || '', in_time: e.in_time || '09:00', out_time: e.out_time || '17:00', completion_remark: '' })));
      }
      if (prev.work_summary?.length) {
        setSummaries(prev.work_summary.map(s => ({ task_name: s.task_name || '', item: s.item || '', quantity_done: '', uom: s.uom || 'Nos' })));
      }
      setCopiedFrom(prev.date);
    } catch (err) { alert(err.response?.data?.detail || 'Could not copy the previous DWR'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanEntries = entries.filter(ent => ent.employee_id || ent.employee_name || ent.task_assigned).map(ent => ({
        ...ent,
        employee_id: ent.employee_id === '__OTHER__' ? '' : ent.employee_id
      }));
      const cleanSummaries = summaries.filter(s => s.task_name || s.item).map(s => ({ ...s, quantity_done: parseFloat(s.quantity_done) || 0 }));
      const payload = { ...form, employee_entries: cleanEntries, work_summary: cleanSummaries };
      if (isEdit) {
        await api.patch(`/dwr/${editData.id}`, payload);
      } else {
        await api.post('/dwr', payload);
      }
      onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      alert(Array.isArray(d) ? d.map(x => x.msg).join(', ') : (d || 'Failed'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" onClick={e => e.stopPropagation()} data-testid="create-dwr-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">{isEdit ? 'Edit Daily Work Report' : 'New Daily Work Report'}</h2>
        {!isEdit && (
          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-md bg-slate-50 border border-slate-200">
            <button type="button" onClick={copyPrevious} data-testid="copy-previous-dwr-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#1B5486]/30 text-[#1B5486] text-xs font-medium hover:bg-[#1B5486]/5">
              <Copy size={13} /> Copy yesterday's DWR
            </button>
            <span className="text-xs text-slate-500" data-testid="copy-previous-status">
              {copiedFrom ? `Copied project, workers and tasks from ${formatDate(copiedFrom)}. Fill in today's quantities and remarks.` : 'Brings over your last DWR before this date (project, workers, tasks).'}
            </span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
              <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} required className="input-field w-full" data-testid="dwr-project-select">
                <option value="">Select</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
                {isEdit && editData.project_id && !projects.some(p => p.id === editData.project_id) && (
                  <option value={editData.project_id}>{editData.project?.project_code || ''} - {editData.project?.name || 'Current project'}</option>
                )}
              </select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-field w-full" data-testid="dwr-date-input" /></div>
          </div>

          <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="input-field w-full" /></div>

          {/* DWR-01: Employee entries with searchable dropdown */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900">Employee Entries</h3>
              <button type="button" onClick={() => setEntries([...entries, { employee_id: '', employee_name: '', task_assigned: '', in_time: '09:00', out_time: '17:00', completion_remark: '' }])}
                className="text-xs text-[#1B5486] hover:underline" data-testid="add-employee-entry-btn">+ Add</button>
            </div>
            <div className="space-y-2">
              {entries.map((ent, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[1.5fr_1fr_0.7fr_0.7fr_auto] gap-2 [&>*]:min-w-0" data-testid={`emp-entry-row-${i}`}>
                  <EmployeeSearchDropdown
                    value={ent.employee_name}
                    employeeId={ent.employee_id}
                    employees={employees}
                    onChange={(id, name) => {
                      const u = [...entries];
                      u[i].employee_id = id;
                      u[i].employee_name = name;
                      setEntries(u);
                    }}
                  />
                  <input placeholder="Task Assigned" value={ent.task_assigned} onChange={e => { const u = [...entries]; u[i].task_assigned = e.target.value; setEntries(u); }} className="input-field text-xs" data-testid={`emp-task-${i}`} />
                  <input type="time" value={ent.in_time} onChange={e => { const u = [...entries]; u[i].in_time = e.target.value; setEntries(u); }} className="input-field text-xs" />
                  <input type="time" value={ent.out_time} onChange={e => { const u = [...entries]; u[i].out_time = e.target.value; setEntries(u); }} className="input-field text-xs" />
                  <button type="button" onClick={() => setEntries(entries.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-xs px-1" data-testid={`remove-emp-entry-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* DWR-02: Work summary with cascading dropdowns */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900">Work Summary</h3>
              <button type="button" onClick={() => setSummaries([...summaries, { task_name: '', item: '', quantity_done: '', uom: 'Nos' }])}
                className="text-xs text-[#1B5486] hover:underline" data-testid="add-work-summary-btn">+ Add</button>
            </div>
            <div className="space-y-2">
              {summaries.map((s, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[1.2fr_1.2fr_0.6fr_0.6fr_auto] gap-2 [&>*]:min-w-0" data-testid={`work-summary-row-${i}`}>
                  <select value={s.task_name} onChange={e => { const u = [...summaries]; u[i].task_name = e.target.value; u[i].item = ''; setSummaries(u); }}
                    className="input-field text-xs" data-testid={`task-name-select-${i}`}>
                    <option value="">Select Task</option>
                    {taskNames.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={s.item} onChange={e => { const u = [...summaries]; u[i].item = e.target.value; setSummaries(u); }}
                    className="input-field text-xs" data-testid={`task-item-select-${i}`}>
                    <option value="">Select Item</option>
                    {(taskItems[s.task_name] || []).map(it => <option key={it} value={it}>{it}</option>)}
                  </select>
                  <input type="number" placeholder="Qty" value={s.quantity_done} onChange={e => { const u = [...summaries]; u[i].quantity_done = e.target.value; setSummaries(u); }}
                    className="input-field text-xs" data-testid={`task-qty-${i}`} />
                  <select value={s.uom} onChange={e => { const u = [...summaries]; u[i].uom = e.target.value; setSummaries(u); }} className="input-field text-xs">
                    {['Nos', 'Mtrs', 'Box', 'Set', 'LS', 'Lot', 'Kg', 'Sqm', 'Rmt'].map(u => <option key={u}>{u}</option>)}
                  </select>
                  <button type="button" onClick={() => setSummaries(summaries.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-xs px-1" data-testid={`remove-work-summary-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-dwr-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create DWR')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
